/**
 * ToggleGroup — Groups multiple Toggle buttons for mutually exclusive or
 * multi-select options.
 *
 * ToggleGroup Component Guidelines
 * - Omit `spacing` for merged-edge toolbar buttons (e.g. text alignment, view mode
 *   switchers). Set `spacing` to a Tailwind spacing value (e.g. 1, 1.5) when options
 *   should be visually separated.
 * - Children should be `<Toggle>` components — ToggleGroup handles selection state
 *   and keyboard navigation across them automatically.
 * - Use ToggleGroup for visual option switching. For form-level single-select with
 *   accessible semantics, prefer RadioGroup instead.
 * @see https://base-ui.com/react/components/toggle-group Base UI ToggleGroup documentation
 * @see https://ui.shadcn.com/docs/components/toggle-group shadcn/ui ToggleGroup documentation
 */
import type { LayoutClassName } from "@replo/design-system/types/layoutClassName";

import React from "react";

import { ToggleGroup as ToggleGroupPrimitive } from "@base-ui/react/toggle-group";
import twMerge from "@replo/design-system/utils/twMerge";

interface ToggleGroupProps<ClassName extends string = string>
  extends Omit<ToggleGroupPrimitive.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  /**
   * Gap between toggle items. When undefined (default), items merge and share edges.
   * Pass a Tailwind spacing scale value (e.g. 1, 1.5, 2) to space items apart.
   */
  spacing?: number;
}

const mergedClasses =
  "[&>[data-slot=toggle]:has(~[data-slot=toggle])]:rounded-r-none [&>[data-slot=toggle]~[data-slot=toggle]]:rounded-l-none [&>[data-slot=toggle]~[data-slot=toggle]]:border-l-0";

const ToggleGroupInner = <ClassName extends string>(
  {
    className,
    UNSAFE_className,
    spacing,
    ...props
  }: ToggleGroupProps<ClassName>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  return (
    <ToggleGroupPrimitive
      ref={ref}
      data-slot="toggle-group"
      style={
        spacing != null
          ? ({ "--gap": `${spacing * 0.25}rem` } as React.CSSProperties)
          : undefined
      }
      className={twMerge(
        "flex w-fit items-stretch [&>*]:focus-visible:z-10 [&>*]:focus-visible:relative",
        spacing != null ? "gap-[var(--gap)]" : mergedClasses,
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
};

const ToggleGroup = React.forwardRef(ToggleGroupInner) as <
  ClassName extends string,
>(
  props: ToggleGroupProps<ClassName> & React.RefAttributes<HTMLDivElement>,
) => React.ReactNode;

export { ToggleGroup };
export type { ToggleGroupProps };
